import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { terms as termsApi } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/common/Button';

// Very small markdown renderer for the terms content: headings (#, ##) and bullet lists.
// Kept intentionally minimal — the content is controlled by us, not user input.
function renderContent(text) {
  const lines = (text || '').split('\n');
  const out = [];
  let list = null;
  const flush = () => { if (list) { out.push(<ul key={`ul-${out.length}`} style={{ margin: '6px 0 12px', paddingLeft: 20, color: '#374151' }}>{list}</ul>); list = null; } };
  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    if (line.startsWith('## ')) { flush(); out.push(<h3 key={i} style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '16px 0 6px' }}>{line.slice(3)}</h3>); }
    else if (line.startsWith('# ')) { flush(); out.push(<h2 key={i} style={{ fontSize: 19, fontWeight: 800, color: '#111827', margin: '0 0 8px' }}>{line.slice(2)}</h2>); }
    else if (line.startsWith('- ')) { (list ||= []).push(<li key={i} style={{ margin: '3px 0', lineHeight: 1.5 }}>{stripBold(line.slice(2))}</li>); }
    else if (line.trim() === '') { flush(); }
    else { flush(); out.push(<p key={i} style={{ margin: '6px 0', color: '#374151', lineHeight: 1.6 }}>{stripBold(line)}</p>); }
  });
  flush();
  return out;
}

// Render **bold** segments inline.
function stripBold(s) {
  const parts = s.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => p.startsWith('**') && p.endsWith('**')
    ? <strong key={i}>{p.slice(2, -2)}</strong>
    : <React.Fragment key={i}>{p}</React.Fragment>);
}

export default function AcceptTerms() {
  const { login, logout, user } = useAuth();
  const [checked, setChecked] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['terms'],
    queryFn: () => termsApi.get().then(r => r.data),
  });

  const handleAccept = async () => {
    setError('');
    setAccepting(true);
    try {
      const res = await termsApi.accept();
      // The accept endpoint returns a fresh auth payload with RequiresTermsAcceptance=false.
      login(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Gabim gjatë pranimit të kushteve.');
      setAccepting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 640, boxShadow: '0 4px 24px #00000015', border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ width: 44, height: 44, background: '#3b82f6', borderRadius: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FileText color="#fff" size={22} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>Kushtet e Përdorimit</h1>
            <p style={{ margin: '2px 0 0', color: '#6b7280', fontSize: 13 }}>
              Mirë se vini{user?.firstName ? `, ${user.firstName}` : ''}! Ju lutemi pranoni kushtet për të vazhduar.
            </p>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px', background: '#fafafa', fontSize: 13.5 }}>
          {isLoading ? <p style={{ color: '#6b7280' }}>Duke ngarkuar…</p> : renderContent(data?.content)}
        </div>

        {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 12px', color: '#dc2626', fontSize: 13, marginTop: 14 }}>{error}</div>}

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, cursor: 'pointer', fontSize: 14, color: '#374151' }}>
          <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} style={{ width: 18, height: 18, cursor: 'pointer' }} />
          I kam lexuar dhe i pranoj Kushtet e Përdorimit dhe Shënimet.
        </label>

        <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
          <Button onClick={handleAccept} disabled={!checked || accepting || isLoading} loading={accepting} size="lg" style={{ flex: 1, justifyContent: 'center' }}>
            Pranoj dhe vazhdo
          </Button>
          <button onClick={logout} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 10, padding: '0 20px', color: '#6b7280', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
            Dil
          </button>
        </div>
      </div>
    </div>
  );
}
